import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Download, FileSpreadsheet, Banknote, Receipt, Wrench, Users, AlertTriangle, Info, Loader2, X, ExternalLink, Send, Sparkles, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, money, downloadPdf } from "@/lib/api";
import { fmtDate } from "@/lib/dateFormat";
import { toast } from "sonner";

const CHART_COLORS = ["#0F766E", "#2563EB", "#D97706", "#9333EA", "#DC2626", "#0891B2", "#65A30D", "#DB2777"];
const RANGES = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["last_month", "Last month"], ["quarter", "Quarter"], ["year", "Year"], ["custom", "Custom"]];
const PAY_STATUSES = ["paid", "partial", "unpaid"];
const JOB_STATUSES = ["open", "in_progress", "awaiting_parts", "completed", "invoiced", "collected", "closed"];

function rangeBounds(key, customStart, customEnd) {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth(); const d = now.getDate();
  let start, end;
  if (key === "today") { start = new Date(y, m, d); end = new Date(y, m, d + 1); }
  else if (key === "week") { const dow = (now.getDay() + 6) % 7; start = new Date(y, m, d - dow); end = new Date(y, m, d - dow + 7); }
  else if (key === "month") { start = new Date(y, m, 1); end = new Date(y, m + 1, 1); }
  else if (key === "last_month") { start = new Date(y, m - 1, 1); end = new Date(y, m, 1); }
  else if (key === "quarter") { const q = Math.floor(m / 3); start = new Date(y, q * 3, 1); end = new Date(y, q * 3 + 3, 1); }
  else if (key === "year") { start = new Date(y, 0, 1); end = new Date(y + 1, 0, 1); }
  else { start = customStart ? new Date(customStart) : new Date(y, m, 1); end = customEnd ? new Date(new Date(customEnd).getTime() + 86400000) : new Date(y, m + 1, 1); }
  return { start: start.toISOString(), end: end.toISOString() };
}

// ---------------- Assistant: suggestions ----------------
const SUGGESTIONS = [
  "How much did I make this month?",
  "How much did tyres make this month?",
  "Which services generated the most revenue?",
  "Show my overdue invoices.",
  "Who owes me the most money?",
  "How much did I spend on parts this month?",
];
const MORE_SUGGESTIONS = [
  "Which technician completed the most jobs?",
  "What were my most profitable jobs?",
  "Compare this month with last month.",
  "Show my monthly expenses.",
  "Which invoices are still unpaid?",
  "How much revenue came from labour versus parts?",
];

// Common garage terminology → canonical work-category stems.
const CATEGORY_SYNONYMS = {
  tyre: ["tyre", "tyres", "tire", "tires"],
  service: ["service", "services", "servicing", "interim service", "full service"],
  mot: ["mot", "m.o.t", "mot test"],
  brake: ["brake", "brakes", "braking", "brake pad", "brake pads", "pads", "discs"],
  clutch: ["clutch", "clutches"],
  diagnostic: ["diagnostic", "diagnostics", "diag", "fault find", "fault finding"],
  repair: ["repair", "repairs", "repaired"],
  exhaust: ["exhaust", "exhausts"],
  battery: ["battery", "batteries"],
  suspension: ["suspension", "shocks", "springs", "shock absorber"],
  "air con": ["aircon", "air con", "air conditioning", "ac regas", "a/c"],
  bodywork: ["bodywork", "body work", "dent", "paintwork"],
  cambelt: ["cambelt", "timing belt", "cam belt"],
  tracking: ["tracking", "wheel alignment", "alignment"],
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Levenshtein distance for light spelling tolerance.
function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++) {
      const c = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + c);
    }
  return d[m][n];
}
function fuzzyTok(q, word) {
  if (word.includes(" ")) return q.includes(word);
  if (q.includes(word)) return true;
  const tol = word.length >= 6 ? 2 : word.length >= 4 ? 1 : 0;
  if (!tol) return false;
  return q.split(/[^a-z0-9]+/).some((t) => t.length >= 3 && lev(t, word) <= tol);
}
const hasKw = (q, list) => list.some((w) => fuzzyTok(q, w));

// Resolve a time period mentioned in the question (null ⇒ use the panel's range).
function questionPeriod(q) {
  const now = new Date(); const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const iso = (dt) => dt.toISOString();
  if (/\b(year to date|ytd)\b/.test(q)) return { label: "year to date", start: iso(new Date(y, 0, 1)), end: iso(new Date(y, m, d + 1)) };
  if (/\byesterday\b/.test(q)) return { label: "yesterday", start: iso(new Date(y, m, d - 1)), end: iso(new Date(y, m, d)) };
  if (/\blast week\b|\bprevious week\b/.test(q)) { const dow = (now.getDay() + 6) % 7; return { label: "last week", start: iso(new Date(y, m, d - dow - 7)), end: iso(new Date(y, m, d - dow)) }; }
  if (/\b(last|past) 7 days\b/.test(q)) return { label: "the last 7 days", start: iso(new Date(y, m, d - 7)), end: iso(new Date(y, m, d + 1)) };
  if (/\b(last|past) 30 days\b/.test(q)) return { label: "the last 30 days", start: iso(new Date(y, m, d - 30)), end: iso(new Date(y, m, d + 1)) };
  if (/\blast month\b|\bprevious month\b/.test(q)) return { label: "last month", rangeKey: "last_month" };
  if (/\btoday\b/.test(q)) return { label: "today", rangeKey: "today" };
  if (/\bthis week\b|\bweek\b/.test(q)) return { label: "this week", rangeKey: "week" };
  if (/\bthis month\b|\bmonth\b/.test(q)) return { label: "this month", rangeKey: "month" };
  if (/\bthis quarter\b|\bquarter\b/.test(q)) return { label: "this quarter", rangeKey: "quarter" };
  if (/\bthis year\b|\byear\b/.test(q)) return { label: "this year", rangeKey: "year" };
  return null;
}

// Map a question to a known work category (synonyms + light fuzzy).
function matchCategory(q, categories) {
  const cats = categories || [];
  for (const c of cats) {
    const n = (c.category || "").toLowerCase();
    if (!n || n === "uncategorised") continue;
    if (q.includes(n) || n.split(/[^a-z0-9]+/).some((w) => w.length >= 3 && fuzzyTok(q, w))) return { cat: c };
  }
  for (const [canon, stems] of Object.entries(CATEGORY_SYNONYMS)) {
    if (hasKw(q, stems)) {
      const c = cats.find((x) => (x.category || "").toLowerCase().includes(canon.split(" ")[0]));
      if (c) return { cat: c };
      return { missing: true, term: canon };
    }
  }
  return null;
}

async function recordsSum(b, kind, extra = "") {
  return api(`/reports/insights/records?start=${encodeURIComponent(b.start)}&end=${encodeURIComponent(b.end)}&kind=${kind}${extra}`);
}

// Interpret the question and build a rich answer from saved figures only.
async function buildAnswer(q, cur, prev, b, periodLabel, ins) {
  const base = { bounds: b, periodLabel };
  const vat = cur.invoiced_vat;

  // Labour vs parts
  if (hasKw(q, ["labour", "labor"]) && hasKw(q, ["parts", "part"])) {
    const L = cur.revenue_by_type.labour, P = cur.revenue_by_type.parts;
    return { ...base, title: "Labour vs parts revenue",
      headline: L === P ? `Labour and parts each generated ${money(L)} (ex-VAT).` : `${L > P ? "Labour" : "Parts"} generated more — ${money(Math.max(L, P))} vs ${money(Math.min(L, P))} (ex-VAT).`,
      calc: `Revenue split from invoice line items in ${periodLabel} (ex-VAT).`,
      breakdown: [{ label: "Labour", value: L, drill: { kind: "labour", title: "Labour lines" } }, { label: "Parts", value: P, drill: { kind: "parts", title: "Parts lines" } }], bars: true,
      links: [{ label: "View labour lines", kind: "labour", title: "Labour lines" }, { label: "View parts lines", kind: "parts", title: "Parts lines" }], exportable: true };
  }
  // Comparison
  if ((/\bcompare\b|\bvs\b|versus|against (last|previous)/.test(q)) && prev) {
    const diff = cur.invoiced_net - prev.invoiced_net;
    return { ...base, title: "Period comparison",
      headline: `Invoiced revenue ${diff >= 0 ? "up" : "down"} ${money(Math.abs(diff))} — ${money(cur.invoiced_net)} this period vs ${money(prev.invoiced_net)} previously (ex-VAT).`,
      delta: { cur: cur.invoiced_net, prev: prev.invoiced_net },
      calc: `Compares ${periodLabel} with the preceding equivalent period. VAT excluded.`,
      breakdown: [
        { label: "Invoiced (this)", value: cur.invoiced_net }, { label: "Invoiced (prev)", value: prev.invoiced_net },
        { label: "Collected (this)", value: cur.collected }, { label: "Collected (prev)", value: prev.collected },
      ],
      links: [{ label: "View invoices", kind: "invoiced", title: "Invoices" }], exportable: true };
  }
  // Who owes the most
  if (/who (owe|owes)|owes me|owe me the most|most money|biggest debtor/.test(q)) {
    const r = await recordsSum(b, "outstanding");
    const by = {}; (r.rows || []).forEach((x) => { const k = x.customer || "Unknown"; by[k] = (by[k] || 0) + (x.balance || 0); });
    const top = Object.entries(by).sort((a, c) => c[1] - a[1]).slice(0, 5);
    return { ...base, title: "Who owes the most",
      headline: top.length ? `${top[0][0]} owes the most — ${money(top[0][1])}.` : "No outstanding balances right now.",
      calc: "Live snapshot of unpaid invoice balances by customer (not period-bound).",
      breakdown: top.map(([label, value]) => ({ label, value })), bars: true,
      links: [{ label: "View outstanding invoices", kind: "outstanding", title: "Outstanding invoices" }] };
  }
  // Technician performance
  if (hasKw(q, ["technician", "mechanic", "engineer", "staff"])) {
    const staff = cur.staff || [];
    if (!staff.length) return { ...base, title: "Technician performance", headline: `No technician data for ${periodLabel}.`, note: "Assign jobs to technicians to track performance.", links: [] };
    const byDone = [...staff].sort((a, c) => c.completed - a.completed);
    const top = byDone[0];
    return { ...base, title: "Technician performance",
      headline: `${top.name} completed the most — ${top.completed} of ${top.jobs} jobs.`,
      calc: `Jobs created in ${periodLabel}, grouped by assigned technician.`,
      breakdown: byDone.slice(0, 5).map((s) => ({ label: s.name, value: s.completed, suffix: " jobs", drill: { kind: "technician", extra: `&technician=${encodeURIComponent(s.id)}`, title: `${s.name} · jobs` } })), bars: true,
      links: [{ label: `View ${top.name}'s jobs`, kind: "technician", extra: `&technician=${encodeURIComponent(top.id)}`, title: `${top.name} · jobs` }] };
  }
  // Most profitable jobs (honest about cost data)
  if (/profitable|most profit|best job/.test(q)) {
    const avail = cur.parts_cost_available || cur.labour_cost_available;
    return { ...base, title: "Most profitable jobs",
      headline: `Gross profit for ${periodLabel} was ${money(cur.gross_profit)} at ${cur.margin}% margin.`,
      calc: "Invoiced revenue minus recorded parts/labour costs (ex-VAT).",
      note: avail ? "Per-job profit reflects only recorded costs — add costs to every job for a complete ranking." : "Per-job profit cannot be ranked yet — add part cost prices and technician hourly rates so costs can be calculated.",
      links: [{ label: "View invoiced jobs", kind: "jobs", title: "Jobs" }], exportable: true };
  }
  // Top categories / which services
  if (/(which|what|top|best|highest|most)/.test(q) && hasKw(q, ["service", "services", "category", "categories", "work", "type"])) {
    const cats = cur.categories || [];
    if (!cats.length) return { ...base, title: "Work categories", headline: `No invoiced revenue in ${periodLabel}.`, links: [] };
    return { ...base, title: "Revenue by work category",
      headline: `${cats[0].category} generated the most — ${money(cats[0].net)} (ex-VAT).`,
      calc: `Invoiced revenue (ex-VAT) by work category in ${periodLabel}.`,
      breakdown: cats.slice(0, 6).map((c) => ({ label: c.category, value: c.net, drill: { kind: "category", extra: `&category=${encodeURIComponent(c.category)}`, title: `Invoices · ${c.category}` } })), bars: true,
      links: [{ label: "View invoices", kind: "invoiced", title: "Invoices" }], exportable: true };
  }
  // Unpaid invoices
  if (/unpaid|still (owe|owing|unpaid)|not paid|awaiting payment/.test(q)) {
    const r = await recordsSum(b, "outstanding");
    const total = (r.rows || []).reduce((s, x) => s + (x.balance || 0), 0);
    return { ...base, title: "Unpaid invoices",
      headline: `${r.count} invoice${r.count === 1 ? "" : "s"} with a balance outstanding, totalling ${money(total)}.`,
      calc: "Live snapshot of invoices with an unpaid or partial balance (not period-bound).",
      links: [{ label: "View unpaid invoices", kind: "outstanding", title: "Outstanding invoices" }] };
  }
  // Overdue
  if (hasKw(q, ["overdue"]) || /past due|late payment/.test(q)) {
    return { ...base, title: "Overdue invoices", value: money(ins.overdue_total),
      headline: `${ins.overdue_count} invoice${ins.overdue_count === 1 ? "" : "s"} overdue, totalling ${money(ins.overdue_total)}.`,
      calc: "Live snapshot of invoices past their due date with a balance (not period-bound).",
      links: [{ label: "View overdue invoices", kind: "overdue", title: "Overdue invoices" }] };
  }
  // Outstanding
  if (hasKw(q, ["outstanding", "owed", "owe"])) {
    return { ...base, title: "Outstanding", value: money(ins.outstanding),
      headline: `Outstanding balance is ${money(ins.outstanding)} across open invoices.`,
      calc: "Live snapshot of all unpaid invoice balances (not period-bound).",
      links: [{ label: "View outstanding invoices", kind: "outstanding", title: "Outstanding invoices" }] };
  }
  // Parts spend (money out)
  if (hasKw(q, ["parts", "part"]) && /spend|spent|cost|costs|buy|bought|purchas/.test(q)) {
    const r = await recordsSum(b, "expenses", "&category=Parts");
    const total = (r.rows || []).reduce((s, x) => s + (x.gross || 0), 0);
    return { ...base, title: "Parts spend", value: money(total),
      headline: `You spent ${money(total)} on parts across ${r.count} expense${r.count === 1 ? "" : "s"} in ${periodLabel}.`,
      calc: `Expenses categorised "Parts" (money out, inc-VAT) dated within ${periodLabel}.`,
      note: cur.parts_cost_available ? `For reference, recorded cost of parts used on invoiced jobs was ${money(cur.parts_cost)}.` : null,
      links: [{ label: "View parts expenses", kind: "expenses", extra: "&category=Parts", title: "Parts expenses" }], exportable: true };
  }
  // Specific work category (tyres, servicing, brakes…)
  const mc = matchCategory(q, cur.categories);
  if (mc) {
    if (mc.missing) return { ...base, title: `${cap(mc.term)} revenue`, headline: `No ${mc.term} revenue recorded in ${periodLabel}.`, calc: `Searched invoiced work categories in ${periodLabel}.`, links: [] };
    const c0 = mc.cat;
    const prevCat = (prev?.categories || []).find((x) => x.category === c0.category);
    return { ...base, title: `${c0.category} revenue`, value: money(c0.net),
      headline: `${c0.category} generated ${money(c0.net)} (ex-VAT) across ${c0.count} invoice${c0.count === 1 ? "" : "s"} in ${periodLabel}.`,
      delta: prevCat ? { cur: c0.net, prev: prevCat.net } : null,
      calc: `Invoiced revenue (ex-VAT) for the ${c0.category} work category in ${periodLabel}. VAT excluded.`,
      links: [{ label: `View ${c0.category} invoices`, kind: "category", extra: `&category=${encodeURIComponent(c0.category)}`, title: `Invoices · ${c0.category}` }], exportable: true };
  }
  // Parts revenue
  if (hasKw(q, ["parts", "part"])) {
    return { ...base, title: "Parts revenue", value: money(cur.revenue_by_type.parts),
      headline: `Parts revenue was ${money(cur.revenue_by_type.parts)} (ex-VAT) in ${periodLabel}.`,
      delta: prev ? { cur: cur.revenue_by_type.parts, prev: prev.revenue_by_type.parts } : null,
      calc: `Revenue from parts invoice lines in ${periodLabel} (ex-VAT).`,
      links: [{ label: "View parts lines", kind: "parts", title: "Parts lines" }], exportable: true };
  }
  // Labour revenue
  if (hasKw(q, ["labour", "labor"])) {
    return { ...base, title: "Labour revenue", value: money(cur.revenue_by_type.labour),
      headline: `Labour revenue was ${money(cur.revenue_by_type.labour)} (ex-VAT) in ${periodLabel}.`,
      delta: prev ? { cur: cur.revenue_by_type.labour, prev: prev.revenue_by_type.labour } : null,
      calc: `Revenue from labour invoice lines in ${periodLabel} (ex-VAT).`,
      links: [{ label: "View labour lines", kind: "labour", title: "Labour lines" }], exportable: true };
  }
  // Cash collected
  if (hasKw(q, ["collected", "collect", "cash", "received", "receive", "takings", "banked", "deposited"]) || /paid in/.test(q)) {
    return { ...base, title: "Cash collected", value: money(cur.collected),
      headline: `You collected ${money(cur.collected)} in payments in ${periodLabel}.`,
      delta: prev ? { cur: cur.collected, prev: prev.collected } : null,
      calc: `Payments received in ${periodLabel} (cash in — may relate to invoices from other periods).`,
      links: [{ label: "View payments", kind: "collected", title: "Payments received" }], exportable: true };
  }
  // Profit / margin
  if (hasKw(q, ["profit", "margin"])) {
    return { ...base, title: "Gross profit", value: money(cur.gross_profit),
      headline: `Gross profit was ${money(cur.gross_profit)} at ${cur.margin}% margin in ${periodLabel}.`,
      delta: prev ? { cur: cur.gross_profit, prev: prev.gross_profit } : null,
      calc: `Invoiced revenue ${money(cur.invoiced_net)} minus recorded costs ${money(cur.parts_cost + (cur.labour_cost_available ? cur.labour_cost : 0))} (ex-VAT).`,
      note: (!cur.parts_cost_available || !cur.labour_cost_available) ? "Some costs are unavailable — add part cost prices and technician hourly rates for a complete margin." : null,
      links: [{ label: "View invoices", kind: "invoiced", title: "Invoices" }], exportable: true };
  }
  // Expenses / spend (general)
  if (hasKw(q, ["expense", "expenses", "spend", "spent", "cost", "costs", "overheads", "outgoings"])) {
    const r = await recordsSum(b, "expenses");
    const total = (r.rows || []).reduce((s, x) => s + (x.gross || 0), 0);
    return { ...base, title: "Expenses", value: money(total),
      headline: `Total expenses were ${money(total)} across ${r.count} record${r.count === 1 ? "" : "s"} in ${periodLabel}.`,
      calc: `Expenses (money out, inc-VAT) dated within ${periodLabel}.`,
      links: [{ label: "View expenses", kind: "expenses", title: "Expenses" }], exportable: true };
  }
  // Jobs
  if (hasKw(q, ["job", "jobs"])) {
    return { ...base, title: "Jobs", value: String(cur.jobs_count),
      headline: `${cur.jobs_count} jobs created in ${periodLabel}, ${cur.jobs_completed} completed.`,
      delta: prev ? { cur: cur.jobs_count, prev: prev.jobs_count } : null,
      calc: `Jobs with a creation date within ${periodLabel}.`,
      links: [{ label: "View jobs", kind: "jobs", title: "Jobs" }], exportable: true };
  }
  // Revenue (default money question)
  if (hasKw(q, ["revenue", "made", "make", "making", "earn", "earned", "earning", "turnover", "sales", "income", "took", "take", "gross"])) {
    return { ...base, title: "Invoiced revenue", value: money(cur.invoiced_net),
      headline: `You invoiced ${money(cur.invoiced_net)} (ex-VAT) across ${cur.invoice_count} invoice${cur.invoice_count === 1 ? "" : "s"} in ${periodLabel}.`,
      delta: prev ? { cur: cur.invoiced_net, prev: prev.invoiced_net } : null,
      calc: `Invoiced revenue (ex-VAT) from invoices issued in ${periodLabel}. VAT ${money(vat)} excluded. Inc-VAT total ${money(cur.invoiced_gross)}.`,
      links: [{ label: "View invoices", kind: "invoiced", title: "Invoices" }], exportable: true };
  }
  return null;
}

// Answer card renderer.
function AssistantAnswer({ ans, onDrill, onExport, onChip }) {
  if (ans.clarify) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="assistant-answer">
        <p className="font-semibold text-midnight">{ans.headline}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {ans.chips.map((c) => (
            <button key={c} onClick={() => onChip(c)} data-testid="assistant-clarify-chip" className="rounded-full border border-brand/40 bg-card px-3 py-1 text-xs font-semibold text-brand hover:bg-brand hover:text-white">{c}</button>
          ))}
        </div>
      </div>
    );
  }
  if (ans.help) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="assistant-answer">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <p className="text-muted-foreground">{ans.headline}</p>
      </div>
    );
  }
  const maxV = ans.bars && ans.breakdown ? Math.max(...ans.breakdown.map((x) => Math.abs(x.value) || 0), 1) : 1;
  return (
    <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-3" data-testid="assistant-answer">
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          {ans.value && <p className="font-display text-2xl font-bold text-midnight" data-testid="assistant-answer-value">{ans.value}</p>}
          <p className="text-sm font-semibold text-midnight">{ans.headline}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{ans.periodLabel}</span>
            {ans.delta && <Delta current={ans.delta.cur} previous={ans.delta.prev} invert={ans.delta.invert} />}
          </div>
          {ans.calc && <p className="mt-1 text-[11px] text-muted-foreground">{ans.calc}</p>}
          {ans.note && <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{ans.note}</p>}
          {ans.breakdown?.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {ans.breakdown.map((it, i) => (
                <button key={it.label + i} onClick={() => it.drill && onDrill(it.drill)} disabled={!it.drill} data-testid="assistant-breakdown-row" className={`block w-full text-left ${it.drill ? "cursor-pointer" : "cursor-default"}`}>
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="truncate capitalize text-midnight">{it.label}</span>
                    <span className="shrink-0 font-semibold text-midnight">{typeof it.value === "number" ? (it.suffix ? it.value + it.suffix : money(it.value)) : it.value}</span>
                  </div>
                  {ans.bars && <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-brand" style={{ width: `${Math.round((Math.abs(it.value) / maxV) * 100)}%` }} /></div>}
                </button>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {(ans.links || []).map((l, i) => (
              <button key={l.kind + i} onClick={() => onDrill(l)} data-testid={`assistant-link-${l.kind}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-semibold text-brand hover:bg-muted"><ExternalLink className="h-3 w-3" />{l.label}</button>
            ))}
            {ans.exportable && (
              <>
                <button onClick={() => onExport("csv", ans.bounds)} data-testid="assistant-export-csv" className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-semibold text-brand-2 hover:bg-muted"><FileSpreadsheet className="h-3 w-3" />CSV</button>
                <button onClick={() => onExport("pdf", ans.bounds)} data-testid="assistant-export-pdf" className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-semibold text-brand-2 hover:bg-muted"><Download className="h-3 w-3" />PDF</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function Delta({ current, previous, invert }) {
  if (previous === null || previous === undefined) return null;
  const diff = (current || 0) - (previous || 0);
  if (Math.abs(diff) < 0.005) return <span className="text-xs text-muted-foreground">no change</span>;
  const up = diff > 0; const good = invert ? !up : up;
  const pct = previous ? Math.round((diff / Math.abs(previous)) * 100) : 100;
  return (<span data-testid="insight-delta" className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? "text-green-600" : "text-red-600"}`}>{up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(pct)}% vs prev</span>);
}

function StatCard({ label, value, prev, invert, testid, hint, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`rounded-xl border border-border bg-card p-4 text-left transition-all ${onClick ? "cursor-pointer hover:border-brand hover:shadow-sm" : ""}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold text-midnight">{value}</p>
      <div className="mt-1 flex items-center gap-2">{prev !== undefined && <Delta current={prev.cur} previous={prev.prev} invert={invert} />}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </button>
  );
}

// --- Drill-down table: columns switch by record kind ---
function DrillTable({ rows, onRecordPayment, onSendReminder, busyId }) {
  if (!rows?.length) return <div className="py-10 text-center text-sm text-muted-foreground">No matching records.</div>;
  const k = rows[0].kind;
  const h = (t) => <th className="px-3 py-2 text-left text-[11px] font-bold uppercase text-muted-foreground">{t}</th>;
  const hr = (t) => <th className="px-3 py-2 text-right text-[11px] font-bold uppercase text-muted-foreground">{t}</th>;
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b border-border">
            {h("Date")}
            {k === "payment" ? h("Invoice") : k === "expense" ? h("Category") : k === "job" ? h("Job") : h("Invoice")}
            {k !== "expense" && h("Customer")}
            {k === "expense" && h("Supplier")}
            {(k === "invoice" || k === "line" || k === "job") && h("VRM")}
            {k === "line" && <>{h("Description")}{hr("Qty")}{hr("Unit")}</>}
            {k === "job" && <>{h("Status")}{hr("Hours")}{hr("Labour")}</>}
            {(k === "invoice" || k === "line" || k === "expense") && hr(k === "expense" ? "Gross" : "Net")}
            {k === "invoice" && <>{hr("Balance")}{h("Status")}</>}
            {k === "payment" && <>{h("Method")}{hr("Amount")}</>}
            {rows[0].days_overdue !== undefined && rows[0].days_overdue !== null && hr("Overdue")}
            {(k === "invoice" || k === "job" || k === "line" || k === "payment") && h("")}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={(r.id || idx) + "-" + idx} className="border-b border-border/50 align-middle" data-testid={`drill-row-${r.id || idx}`}>
              <td className="px-3 py-2 whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
              {k === "expense" ? <td className="px-3 py-2 capitalize">{(r.category || "").replace(/_/g, " ")}</td>
                : <td className="px-3 py-2 font-semibold text-midnight">{r.ref || "—"}</td>}
              {k !== "expense" && <td className="px-3 py-2">{r.customer || "—"}</td>}
              {k === "expense" && <td className="px-3 py-2">{r.supplier || "—"}</td>}
              {(k === "invoice" || k === "line" || k === "job") && <td className="px-3 py-2 uppercase">{r.vrm || "—"}</td>}
              {k === "line" && <><td className="px-3 py-2">{r.description}</td><td className="px-3 py-2 text-right">{r.qty}</td><td className="px-3 py-2 text-right">{money(r.unit_price)}</td></>}
              {k === "job" && <><td className="px-3 py-2 capitalize">{(r.status || "").replace(/_/g, " ")}</td><td className="px-3 py-2 text-right">{r.hours}</td><td className="px-3 py-2 text-right">{money(r.labour_sales)}</td></>}
              {(k === "invoice" || k === "line") && <td className="px-3 py-2 text-right font-semibold">{money(r.net)}</td>}
              {k === "expense" && <td className="px-3 py-2 text-right font-semibold">{money(r.gross)}</td>}
              {k === "invoice" && <><td className="px-3 py-2 text-right">{money(r.balance)}</td><td className="px-3 py-2 capitalize">{r.status}</td></>}
              {k === "payment" && <><td className="px-3 py-2 capitalize">{(r.method || "").replace(/_/g, " ")}{r.is_refund ? " (refund)" : ""}</td><td className="px-3 py-2 text-right font-semibold">{money(r.amount)}</td></>}
              {r.days_overdue !== undefined && r.days_overdue !== null && <td className="px-3 py-2 text-right font-semibold text-red-600">{r.days_overdue}d</td>}
              {(k === "invoice" || k === "line" || k === "payment") && r.invoice_id && (
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                    <Link to={`/invoice/${r.invoice_id}`} title="Open invoice" data-testid={`drill-open-inv-${r.invoice_id}`} className="text-brand hover:text-brand-2"><ExternalLink className="h-4 w-4" /></Link>
                    {r.customer_id && <Link to={`/customers/${r.customer_id}`} title="View customer" className="text-muted-foreground hover:text-brand"><Users className="h-4 w-4" /></Link>}
                    {onRecordPayment && r.days_overdue !== undefined && r.days_overdue !== null && (
                      <>
                        <button onClick={() => onRecordPayment(r)} data-testid={`drill-pay-${r.invoice_id}`} className="rounded-md border border-border px-2 py-1 text-xs font-semibold text-brand hover:bg-muted">Record payment</button>
                        <button onClick={() => onSendReminder(r)} disabled={busyId === r.invoice_id} data-testid={`drill-remind-${r.invoice_id}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-semibold text-brand-2 hover:bg-muted">{busyId === r.invoice_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}Reminder</button>
                      </>
                    )}
                  </div>
                </td>
              )}
              {k === "job" && <td className="px-3 py-2 text-right">{r.job_id && <Link to={`/jobs/${r.job_id}`} title="Open job" data-testid={`drill-open-job-${r.job_id}`} className="text-brand hover:text-brand-2"><ExternalLink className="ml-auto h-4 w-4" /></Link>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecordPaymentMini({ row, onClose, onDone }) {
  const [amount, setAmount] = useState(row.balance ? String(row.balance) : "");
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api("/payments", { method: "POST", body: JSON.stringify({ invoice_id: row.invoice_id, amount: amt, method }) });
      toast.success("Payment recorded");
      onDone();
    } catch (e) { toast.error(e.message || "Could not record payment"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="drill-record-payment">
        <DialogHeader><DialogTitle>Record payment · {row.ref}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Outstanding balance {money(row.balance)}. Uses the existing payment workflow — no duplicate records.</p>
          <label className="block text-sm"><span className="mb-1 block font-semibold">Amount (£)</span><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="mini-pay-amount" className="w-full rounded-lg border border-border px-3 py-2" /></label>
          <label className="block text-sm"><span className="mb-1 block font-semibold">Method</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="mini-pay-method" className="w-full rounded-lg border border-border px-3 py-2">
              {["card", "cash", "bank_transfer", "cheque", "other"].map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="mini-pay-confirm" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReportsInsights() {
  const [range, setRange] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ category: "", technician: "", method: "", payment_status: "", job_status: "" });
  const [overdueSnap, setOverdueSnap] = useState(null);
  const [drill, setDrill] = useState(null); // {kind, title, rows, loading, params}
  const [payRow, setPayRow] = useState(null);
  const [busyRemind, setBusyRemind] = useState(null);
  const [assistQ, setAssistQ] = useState("");
  const [assistAns, setAssistAns] = useState(null);
  const [assistBusy, setAssistBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const bounds = useMemo(() => rangeBounds(range, customStart, customEnd), [range, customStart, customEnd]);
  const filterQs = useMemo(() => Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join(""), [filters]);
  const baseQs = `start=${encodeURIComponent(bounds.start)}&end=${encodeURIComponent(bounds.end)}`;
  const activeFilters = Object.values(filters).filter(Boolean).length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`/reports/insights?${baseQs}&compare=true${filterQs}`);
      setData(d);
      const od = await api(`/reports/insights/records?${baseQs}&kind=overdue${filterQs}`);
      setOverdueSnap({ count: od.count, total: (od.rows || []).reduce((s, r) => s + (r.balance || 0), 0) });
    } catch (e) { toast.error(e.message || "Failed to load insights"); }
    finally { setLoading(false); }
  }, [baseQs, filterQs]);
  useEffect(() => { if (range === "custom" && (!customStart || !customEnd)) return; load(); }, [bounds.start, bounds.end, filterQs]);

  const openDrill = async (kind, extra = "", title = "", opts = {}) => {
    const b = opts.bounds || bounds;
    const useFilters = opts.useFilters !== false;
    const bq = `start=${encodeURIComponent(b.start)}&end=${encodeURIComponent(b.end)}`;
    const fq = useFilters ? filterQs : "";
    setDrill({ kind, title: title || kind, rows: [], loading: true });
    try {
      const r = await api(`/reports/insights/records?${bq}&kind=${kind}${fq}${extra}`);
      setDrill({ kind: r.kind, title: title || r.title, rows: r.rows, loading: false });
    } catch (e) { toast.error(e.message || "Failed to load records"); setDrill(null); }
  };

  const sendReminder = async (row) => {
    setBusyRemind(row.invoice_id);
    try {
      await api(`/invoices/${row.invoice_id}/overdue-reminder`, { method: "POST", body: JSON.stringify({}) });
      toast.success(`Reminder emailed for ${row.ref}`);
    } catch (e) { toast.error(e.message || "Could not send reminder"); }
    finally { setBusyRemind(null); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadPdf(`/reports/insights/export?${baseQs}`, "insights.csv"); toast.success("CSV downloaded"); }
    catch (e) { toast.error(e.message || "Export failed (Pro plan required)"); }
    finally { setExporting(false); }
  };
  const exportMonthlyPdf = async () => {
    const d0 = new Date(bounds.start);
    try { await downloadPdf(`/reports/accounting?month=${d0.getMonth() + 1}&year=${d0.getFullYear()}&format=pdf`, `report-${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, "0")}.pdf`); toast.success("Monthly report downloaded"); }
    catch (e) { toast.error(e.message || "Export failed (Pro plan required)"); }
  };

  const runAssistant = async (rawQ) => {
    const question = (typeof rawQ === "string" ? rawQ : assistQ).trim();
    if (!question) return;
    if (typeof rawQ === "string") setAssistQ(rawQ);
    setAssistBusy(true);
    setAssistAns(null);
    try {
      const q = question.toLowerCase();
      const per = questionPeriod(q);
      let b, periodLabel;
      if (per) {
        b = per.rangeKey ? rangeBounds(per.rangeKey) : { start: per.start, end: per.end };
        periodLabel = per.label;
      } else {
        b = bounds;
        periodLabel = (RANGES.find((r) => r[0] === range) || [])[1] || "this period";
      }

      // Ambiguous "last" with no clear noun → ask a short clarification.
      if (/\blast\b/.test(q) && !per && /revenue|made|make|profit|spend|spent|collect|owe|invoice|expense|job/.test(q)) {
        setAssistAns({ clarify: true, headline: "Which period did you mean?", chips: [
          question.replace(/\blast\b/i, "last week"),
          question.replace(/\blast\b/i, "last month"),
          question.replace(/\blast\b/i, "last year"),
        ] });
        return;
      }

      const ins = await api(`/reports/insights?start=${encodeURIComponent(b.start)}&end=${encodeURIComponent(b.end)}&compare=true`);
      const cur = ins.current, prev = ins.previous;

      // Sync the panel view to the asked period.
      if (per?.rangeKey && per.rangeKey !== range) setRange(per.rangeKey);
      else if (per && !per.rangeKey) {
        setRange("custom");
        setCustomStart(b.start.slice(0, 10));
        setCustomEnd(new Date(new Date(b.end).getTime() - 86400000).toISOString().slice(0, 10));
      }

      if (!cur) { setAssistAns({ help: true, headline: `No data for ${periodLabel}.` }); return; }
      const ans = await buildAnswer(q, cur, prev, b, periodLabel, ins);
      setAssistAns(ans || { help: true, headline: "I didn't quite catch that — try one of the suggested questions below. I report saved figures for revenue, cash collected, outstanding, overdue, profit, labour, parts, jobs, expenses, technicians and any work category." });
    } catch (e) {
      toast.error(e.message || "Could not answer");
    } finally {
      setAssistBusy(false);
    }
  };

  const assistExport = async (fmt, b) => {
    try {
      if (fmt === "csv") { await downloadPdf(`/reports/insights/export?start=${encodeURIComponent(b.start)}&end=${encodeURIComponent(b.end)}`, "insights.csv"); toast.success("CSV downloaded"); }
      else { const d0 = new Date(b.start); await downloadPdf(`/reports/accounting?month=${d0.getMonth() + 1}&year=${d0.getFullYear()}&format=pdf`, `report-${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, "0")}.pdf`); toast.success("PDF downloaded"); }
    } catch (e) { toast.error(e.message || "Export failed (Pro plan required)"); }
  };

  const c = data?.current;
  const p = data?.previous;
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ category: "", technician: "", method: "", payment_status: "", job_status: "" });

  return (
    <div data-testid="reports-insights">
      {/* Range + exports */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map(([k, lbl]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`} className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${range === k ? "bg-brand text-white" : "border border-border text-muted-foreground hover:bg-muted"}`}>{lbl}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={exportCsv} disabled={exporting} data-testid="insights-export-csv" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-brand hover:bg-muted">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}CSV</button>
          <button onClick={exportMonthlyPdf} data-testid="insights-export-pdf" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-brand hover:bg-muted"><Download className="h-4 w-4" />Monthly PDF</button>
        </div>
      </div>

      {range === "custom" && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="custom-range">
          <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start" className="rounded-lg border border-border px-3 py-2 text-sm" />
          <span className="text-muted-foreground">to</span>
          <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end" className="rounded-lg border border-border px-3 py-2 text-sm" />
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid="insights-filters">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select value={filters.category} onChange={(e) => setF("category", e.target.value)} data-testid="filter-category" className="rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">All categories</option>
          {(c?.categories || []).map((x) => <option key={x.category} value={x.category}>{x.category.replace(/_/g, " ")}</option>)}
        </select>
        <select value={filters.technician} onChange={(e) => setF("technician", e.target.value)} data-testid="filter-technician" className="rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">All technicians</option>
          {(c?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.method} onChange={(e) => setF("method", e.target.value)} data-testid="filter-method" className="rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">All methods</option>
          {(c?.payment_methods || []).map((m) => <option key={m.method} value={m.method}>{m.method.replace(/_/g, " ")}</option>)}
        </select>
        <select value={filters.payment_status} onChange={(e) => setF("payment_status", e.target.value)} data-testid="filter-payment-status" className="rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">All payment status</option>
          {PAY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.job_status} onChange={(e) => setF("job_status", e.target.value)} data-testid="filter-job-status" className="rounded-lg border border-border px-2 py-1.5 text-sm">
          <option value="">All job status</option>
          {JOB_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        {activeFilters > 0 && <button onClick={clearFilters} data-testid="filter-clear" className="ml-1 inline-flex items-center gap-1 text-sm font-semibold text-brand-2 hover:underline"><X className="h-3.5 w-3.5" />Clear ({activeFilters})</button>}
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading insights…</div>
      ) : !c ? (
        <div className="py-16 text-center text-muted-foreground">No data for this period.</div>
      ) : (
        <>
          {/* Assistant */}
          <div className="mb-4 rounded-xl border border-border bg-card p-3" data-testid="insights-assistant">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand" />
              <input value={assistQ} onChange={(e) => setAssistQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runAssistant()} data-testid="assistant-input" placeholder='Ask e.g. "How much did tyres make this month?"' className="min-w-0 flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
              <button onClick={() => runAssistant()} disabled={assistBusy} data-testid="assistant-ask" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-bold text-white disabled:opacity-60">{assistBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Ask</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assistant-suggestions">
              {[...SUGGESTIONS, ...(showMore ? MORE_SUGGESTIONS : [])].map((s) => (
                <button key={s} onClick={() => runAssistant(s)} disabled={assistBusy} data-testid="assistant-suggestion" className="rounded-full border border-border bg-secondary/50 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-60">{s}</button>
              ))}
              <button onClick={() => setShowMore((v) => !v)} data-testid="assistant-more" className="rounded-full px-2.5 py-1 text-xs font-semibold text-brand hover:underline">{showMore ? "Fewer questions" : "More questions"}</button>
            </div>
            {assistAns && <AssistantAnswer ans={assistAns} onChip={(t) => runAssistant(t)} onDrill={(l) => openDrill(l.kind, l.extra || "", l.title, { bounds: assistAns.bounds, useFilters: false })} onExport={assistExport} />}
          </div>

          {/* Headline stats — clickable */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard testid="stat-invoiced" label="Invoiced (ex-VAT)" value={money(c.invoiced_net)} prev={{ cur: c.invoiced_net, prev: p?.invoiced_net }} hint={`${c.invoice_count} invoices · inc-VAT ${money(c.invoiced_gross)}`} onClick={() => openDrill("invoiced", "", "Invoices")} />
            <StatCard testid="stat-collected" label="Cash collected" value={money(c.collected)} prev={{ cur: c.collected, prev: p?.collected }} hint="Payments received" onClick={() => openDrill("collected", "", "Payments received")} />
            <StatCard testid="stat-profit" label="Gross profit (ex-VAT)" value={money(c.gross_profit)} prev={{ cur: c.gross_profit, prev: p?.gross_profit }} hint={`Margin ${c.margin}% · costs ${money(c.parts_cost + c.labour_cost)}`} onClick={() => openDrill("invoiced", "", "Invoices behind gross profit")} />
            <StatCard testid="stat-overdue" label="Overdue" value={overdueSnap ? money(overdueSnap.total) : "—"} invert hint={overdueSnap ? `${overdueSnap.count} invoices past due` : "snapshot"} onClick={() => openDrill("overdue", "", "Overdue invoices")} />
            <StatCard testid="stat-outstanding" label="Outstanding" value={money(data.outstanding)} invert hint="Unpaid balances (snapshot)" onClick={() => openDrill("outstanding", "", "Outstanding invoices")} />
            <StatCard testid="stat-avg-invoice" label="Avg invoice" value={money(c.avg_invoice)} prev={{ cur: c.avg_invoice, prev: p?.avg_invoice }} onClick={() => openDrill("invoiced", "", "Invoices")} />
            <StatCard testid="stat-jobs" label="Jobs created" value={c.jobs_count} prev={{ cur: c.jobs_count, prev: p?.jobs_count }} hint={`${c.jobs_completed} completed`} onClick={() => openDrill("jobs", "", "Jobs")} />
            <StatCard testid="stat-vat" label="VAT on invoices" value={money(c.invoiced_vat)} hint="Excluded from profit" onClick={() => openDrill("invoiced", "", "Invoices")} />
          </div>

          {(!c.parts_cost_available || !c.labour_cost_available) && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="cost-availability-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                {!c.parts_cost_available && <p>Parts cost is unavailable — add a <b>cost price</b> to part lines on jobs to calculate parts margin.</p>}
                {!c.labour_cost_available && <p>Labour cost is unavailable — set a technician <b>hourly rate</b> and assign jobs. Gross profit currently reflects only recorded costs.</p>}
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="chart-categories">
              <p className="mb-3 flex items-center gap-2 text-sm font-bold text-midnight"><Wrench className="h-4 w-4 text-brand" />Revenue by work category (ex-VAT)</p>
              {c.categories.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No invoiced revenue in this period.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={c.categories.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }} onClick={(e) => { const cat = e?.activePayload?.[0]?.payload?.category; if (cat) openDrill("category", `&category=${encodeURIComponent(cat)}`, `Invoices · ${cat}`); }}>
                    <CartesianGrid horizontal={false} stroke="#eef2f5" />
                    <XAxis type="number" tickFormatter={(v) => `£${v}`} fontSize={11} />
                    <YAxis type="category" dataKey="category" width={110} fontSize={11} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Bar dataKey="net" radius={[0, 4, 4, 0]} cursor="pointer">
                      {c.categories.slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="chart-methods">
              <p className="mb-3 flex items-center gap-2 text-sm font-bold text-midnight"><Banknote className="h-4 w-4 text-brand" />Cash collected by method</p>
              {c.payment_methods.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No payments in this period.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={c.payment_methods.filter((m) => m.amount > 0)} dataKey="amount" nameKey="method" cx="50%" cy="50%" outerRadius={90} label={(e) => e.method.replace(/_/g, " ")} cursor="pointer" onClick={(e) => e?.method && openDrill("method", `&method=${encodeURIComponent(e.method)}`, `Payments · ${e.method}`)}>
                      {c.payment_methods.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Revenue by type — clickable */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <button onClick={() => openDrill("labour", "", "Labour lines")} data-testid="rev-labour" className="rounded-xl border border-border bg-card p-4 text-left hover:border-brand"><p className="text-xs font-semibold uppercase text-muted-foreground">Labour revenue</p><p className="mt-1 text-xl font-bold text-midnight">{money(c.revenue_by_type.labour)}</p></button>
            <button onClick={() => openDrill("parts", "", "Parts lines")} data-testid="rev-parts" className="rounded-xl border border-border bg-card p-4 text-left hover:border-brand"><p className="text-xs font-semibold uppercase text-muted-foreground">Parts revenue</p><p className="mt-1 text-xl font-bold text-midnight">{money(c.revenue_by_type.parts)}</p></button>
            <button onClick={() => openDrill("expenses", "", "Expenses")} data-testid="rev-expenses" className="rounded-xl border border-border bg-card p-4 text-left hover:border-brand"><p className="text-xs font-semibold uppercase text-muted-foreground">Expenses & spend</p><p className="mt-1 text-xl font-bold text-midnight">View records</p></button>
          </div>

          {/* Category table — clickable rows */}
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card" data-testid="table-categories">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-bold text-midnight"><Receipt className="h-4 w-4 text-brand" />Work categories<span className="text-xs font-normal text-muted-foreground">· click for job & invoice detail</span></div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground"><th className="px-4 py-2">Category</th><th className="px-4 py-2 text-right">Invoices</th><th className="px-4 py-2 text-right">Net (ex-VAT)</th></tr></thead>
              <tbody>
                {c.categories.length === 0 ? <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">No data</td></tr> :
                  c.categories.map((cat) => (
                    <tr key={cat.category} onClick={() => openDrill("category", `&category=${encodeURIComponent(cat.category)}`, `Invoices · ${cat.category}`)} className="cursor-pointer border-b border-border/50 hover:bg-secondary" data-testid={`cat-row-${cat.category}`}>
                      <td className="px-4 py-2 capitalize">{cat.category.replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 text-right">{cat.count}</td>
                      <td className="px-4 py-2 text-right font-semibold">{money(cat.net)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Staff performance — clickable rows */}
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card" data-testid="table-staff">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-bold text-midnight"><Users className="h-4 w-4 text-brand" />Technician performance<span className="text-xs font-normal text-muted-foreground">· jobs in period</span></div>
            {c.staff.length === 0 ? (
              <div className="flex items-start gap-2 px-4 py-6 text-sm text-muted-foreground"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />No technician data for this period. Assign jobs to technicians to track performance.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground"><th className="px-4 py-2">Technician</th><th className="px-4 py-2 text-right">Jobs</th><th className="px-4 py-2 text-right">Completed</th><th className="px-4 py-2 text-right">Labour hrs</th><th className="px-4 py-2 text-right">Labour sales</th></tr></thead>
                <tbody>
                  {c.staff.map((s) => (
                    <tr key={s.id} onClick={() => openDrill("technician", `&technician=${encodeURIComponent(s.id)}`, `${s.name} · jobs`)} className="cursor-pointer border-b border-border/50 hover:bg-secondary" data-testid={`staff-row-${s.id}`}>
                      <td className="px-4 py-2">{s.name}</td><td className="px-4 py-2 text-right">{s.jobs}</td><td className="px-4 py-2 text-right">{s.completed}</td><td className="px-4 py-2 text-right">{s.hours}</td><td className="px-4 py-2 text-right font-semibold">{money(s.labour_sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="mt-3 text-[11px] text-muted-foreground">Invoiced revenue reflects invoices issued in the period; cash collected reflects payments received. Profit excludes VAT and uses only recorded parts/labour costs. Outstanding & overdue are live snapshots. Figures are never double-counted across jobs, invoices or payments.</p>
        </>
      )}

      {/* Drill-down dialog */}
      {drill && (
        <Dialog open onOpenChange={() => setDrill(null)}>
          <DialogContent className="max-w-5xl" data-testid="drill-dialog">
            <DialogHeader><DialogTitle className="capitalize">{drill.title} {!drill.loading && <span className="text-sm font-normal text-muted-foreground">· {drill.rows.length} record{drill.rows.length === 1 ? "" : "s"}</span>}</DialogTitle></DialogHeader>
            <div className="max-h-[65vh] overflow-auto">
              {drill.loading ? <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading…</div>
                : <DrillTable rows={drill.rows} onRecordPayment={(r) => setPayRow(r)} onSendReminder={sendReminder} busyId={busyRemind} />}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {payRow && <RecordPaymentMini row={payRow} onClose={() => setPayRow(null)} onDone={() => { setPayRow(null); load(); if (drill) openDrill(drill.kind, "", drill.title); }} />}
    </div>
  );
}
